import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

export interface EmailTemplate {
  id: string
  name: string
  subject: string | null
  body_html: string | null
  body_text: string | null
  category: string | null
  is_shared: boolean
  user_id: string | null
  is_active: boolean
  usage_count: number
  variables: string[]
  created_at: string
  updated_at: string
}

interface EmailTemplateFilters {
  category?: string
  search?: string
}

export function useEmailTemplates(filters: EmailTemplateFilters = {}) {
  const params = new URLSearchParams()
  if (filters.category) params.set("category", filters.category)
  if (filters.search) params.set("search", filters.search)
  const q = params.toString()

  return useQuery({
    queryKey: ["email_templates", filters],
    queryFn: () =>
      api.get<{ data: EmailTemplate[] }>(`/api/v1/email_templates${q ? `?${q}` : ""}`),
  })
}

export function useEmailTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ["email_templates", id],
    queryFn: () => api.get<{ data: EmailTemplate }>(`/api/v1/email_templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EmailTemplate>) =>
      api.post<{ data: EmailTemplate }>("/api/v1/email_templates", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_templates"] })
    },
  })
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailTemplate> }) =>
      api.patch<{ data: EmailTemplate }>(`/api/v1/email_templates/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_templates"] })
    },
  })
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/email_templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_templates"] })
    },
  })
}

export function useRenderEmailTemplate() {
  return useMutation({
    mutationFn: ({ id, variables }: { id: string; variables: Record<string, string> }) =>
      api.post<{ data: { subject: string; body_html: string; body_text: string } }>(
        `/api/v1/email_templates/${id}/render_template`,
        { variables }
      ),
  })
}

export function useEmailTemplateUse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/email_templates/${id}/use`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_templates"] })
    },
  })
}
